import {
    actionDisplayNameForModel,
    canonicalActionTagFor,
    findModelByActionTag,
    getActionPricingContext,
    getPrivateModelCostWithDuration,
    getPublicModelCostWithDuration,
    normalizeActionTag,
} from 'state/aiModels'
import { inferActionCommandModelType } from 'components/shared/tiptap/actionCommandIconFlow'
import { getDefaultAspectRatioForActionCommand } from 'utils/actionCommandMetadata'
import { parseActionCommandFromContent } from 'utils/actionCommandFeeConfirmation'
import { isPublicAiPricingContext } from 'utils/questPricingContext'
import { videoKeyframeInputs } from 'utils/aiVideoKeyframes'
import {
    COMMAND_TAG_PREVIEW_CONFIG_OVERRIDES,
    isKnownActionTag,
} from 'components/answers/knownActionTags'
import {
    isSimplePostMediaAction,
    postMediaActionDisplayName,
    postMediaActionKindForTag,
    postMediaSourceReference,
    postMediaTargetActionTag,
    resolvePostMediaActionPrompt,
} from 'components/answers/postMediaActions'

const COMMAND_TAG_DISPLAY_NAME_OVERRIDES = {
    extend: 'Extend',
    gif: 'GIF',
    nb: 'Nano Banana 2',
    nbpro: 'Nano Banana Pro',
    screenshot: 'Screenshot',
    stitch: 'Smart Stitch',
}

const formatAiActionPrice = price => {
    const numericPrice = price == null ? Number.NaN : Number(price)
    if (!Number.isFinite(numericPrice)) return null
    if (numericPrice <= 0) return 'FREE'
    return `$${numericPrice.toFixed(2)}`
}

const aiActionModelDisplayName = (model, fallbackTag) => {
    const tag = normalizeActionTag(fallbackTag)
    return COMMAND_TAG_DISPLAY_NAME_OVERRIDES[tag] || actionDisplayNameForModel(model, tag) || tag
}

const aiActionTokenDisplayName = (model, fallbackTag) => {
    const tag = normalizeActionTag(fallbackTag)
    return actionDisplayNameForModel(model, tag)
}

const modelSupportsReferences = model => {
    if (!model || model.modelType !== 'video') return false
    return (
        Number(model.referenceImageLimit || 0) > 0 ||
        Number(model.referenceVideoLimit || 0) > 0 ||
        Number(model.referenceAudioLimit || 0) > 0
    )
}

const actionPreviewConfig = (tag, model = null) => {
    const normalizedTag = normalizeActionTag(tag)
    const override = COMMAND_TAG_PREVIEW_CONFIG_OVERRIDES[normalizedTag] || {}

    if (override.supportedInputs) {
        return {
            ...override,
            supportedInputs: new Set(override.supportedInputs),
        }
    }

    if (!model) {
        return {
            ...override,
            supportedInputs: new Set(['text', 'image', 'video', 'audio']),
        }
    }

    if (/^(edit|animate)(_|$)/.test(normalizedTag)) {
        return {
            ...override,
            supportedInputs: new Set(['text', 'image']),
        }
    }

    if (model.modelType === 'image' || model.modelType === 'video') {
        return {
            ...override,
            supportedInputs: new Set(['text', 'image']),
        }
    }

    const supportedInputs = new Set()
    if (model.canRemixVideo) supportedInputs.add('video')
    if (model.canRemixImage) supportedInputs.add('image')
    if (model.canRemixAudio) supportedInputs.add('audio')

    if (!model.canRemixVideo && !model.canRemixAudio) {
        supportedInputs.add('text')
    }

    return {
        ...override,
        supportedInputs,
    }
}

const legacyActionFromAnswerContent = (answer, tagOverride = null) => {
    const content = answer?.content || answer?.display_content || ''
    const tag = normalizeActionTag(tagOverride) || parseActionCommandFromContent(content)
    if (!tag) return null

    const prompt =
        typeof content === 'string'
            ? content
                  .replace(
                      /(^|[\s\n])![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?=[\s\n.,;:!?]|$)/g,
                      '$1',
                  )
                  .trim()
            : ''

    return {
        tag,
        type: 'model',
        model_type: inferActionCommandModelType(tag),
        prompt,
        inputs: prompt
            ? {
                  text: {
                      value: prompt,
                  },
              }
            : {},
        settings: {},
    }
}

export const aiActionToPreviewProps = ({ action, run = null, aiModels = [], quest = null }) => {
    const runAction = run?.action_payload || run?.actionPayload || {}
    const actionSettings = action?.settings || {}
    const runSettings = runAction?.settings || {}
    const settings = {
        ...runSettings,
        ...actionSettings,
    }
    const actionInputs = action?.inputs || {}
    const runInputs = runAction?.inputs || {}
    const inputs = {
        ...runInputs,
        ...actionInputs,
        references: {
            ...(runInputs?.references || {}),
            ...(actionInputs?.references || {}),
        },
    }
    const sourceAction = {
        ...runAction,
        ...(action || {}),
        settings,
        inputs,
    }
    const tag = normalizeActionTag(sourceAction?.tag || run?.action_tag)
    if (!tag) return null

    const simpleMode = isSimplePostMediaAction(sourceAction)
    const targetPostMediaTag = postMediaTargetActionTag(sourceAction)
    const model =
        (sourceAction?.ai_model_id &&
            (aiModels || []).find(candidate => candidate?.id === sourceAction.ai_model_id)) ||
        findModelByActionTag(aiModels || [], targetPostMediaTag || tag)
    const extendAiModel =
        tag === 'extend' && settings?.extend_ai_model_id
            ? (aiModels || []).find(candidate => candidate?.id === settings.extend_ai_model_id)
            : null
    const modelTag =
        targetPostMediaTag ||
        (tag === 'extend' ? canonicalActionTagFor(settings?.extend_action_tag) || tag : tag)
    const pricedModel = extendAiModel || findModelByActionTag(aiModels || [], modelTag) || model
    const config = actionPreviewConfig(tag, model)
    const defaultSettings = config.defaultSettings || {}
    const references = inputs?.references || {}
    const keyframes = videoKeyframeInputs(tag, inputs)
    const displayImageInput = keyframes.startImage || inputs?.image
    const displayReferenceImages = keyframes.frameMode
        ? keyframes.endImage
            ? [keyframes.endImage]
            : []
        : references?.images || []
    const textValue = simpleMode
        ? resolvePostMediaActionPrompt({ action, runAction })
        : inputs?.text?.value || sourceAction?.prompt || null
    const durationSeconds = settings.duration_seconds ?? null
    const defaultAspectRatio =
        sourceAction?.default_aspect_ratio ??
        settings.default_aspect_ratio ??
        getDefaultAspectRatioForActionCommand(pricedModel?.actionTagName || tag)
    const generationCount =
        sourceAction?.generation_count ??
        sourceAction?.generationCount ??
        settings.generation_count ??
        1
    const pricingContext = getActionPricingContext(pricedModel, sourceAction)
    const price = pricedModel
        ? isPublicAiPricingContext(quest)
            ? getPublicModelCostWithDuration(pricedModel, durationSeconds, pricingContext)
            : getPrivateModelCostWithDuration(pricedModel, durationSeconds, pricingContext)
        : null
    const actionPriceLabel = action?.price_label || action?.priceLabel
    const runPriceLabel =
        run?.price_label || run?.priceLabel || runAction?.price_label || runAction?.priceLabel
    const numericPrice = price == null ? Number.NaN : Number(price)
    const computedPriceLabel = Number.isFinite(numericPrice)
        ? formatAiActionPrice(numericPrice * generationCount)
        : null
    const priceLabel = computedPriceLabel || actionPriceLabel || runPriceLabel
    const supportedInputs = config.supportedInputs || new Set()
    const supportsTextInput = supportedInputs.has('text')
    const supportsImageInput = supportedInputs.has('image')
    const supportsVideoInput = supportedInputs.has('video')
    const supportsAudioInput = supportedInputs.has('audio')
    const supportsVideoListInput = tag === 'stitch'
    const supportsReferenceInputs =
        config.supportsReferences === false
            ? false
            : config.supportsReferences === true || modelSupportsReferences(pricedModel)
    const tokenDisplayName =
        postMediaActionDisplayName(sourceAction) || aiActionTokenDisplayName(model, tag)
    return {
        tag,
        previewTitle: sourceAction?.preview_title || sourceAction?.previewTitle || null,
        displayName:
            tag === 'extend'
                ? 'Extend Video'
                : tokenDisplayName ||
                  aiActionModelDisplayName(pricedModel || model, modelTag || tag),
        tokenDisplayName,
        simpleMode,
        simpleKind: simpleMode
            ? sourceAction?.craft_context?.kind || postMediaActionKindForTag(tag) || null
            : null,
        sourcePostReference: simpleMode ? postMediaSourceReference(sourceAction) : null,
        priceLabel,
        type: simpleMode
            ? sourceAction?.type || sourceAction?.kind || 'tool'
            : model || config.modelType
            ? 'model'
            : sourceAction?.type || 'model',
        modelType:
            sourceAction?.model_type ||
            run?.model_type ||
            model?.modelType ||
            config.modelType ||
            inferActionCommandModelType(tag),
        defaultDurationSeconds:
            sourceAction?.default_duration_seconds ??
            settings.default_duration_seconds ??
            pricedModel?.defaultDurationSeconds ??
            null,
        defaultAspectRatio,
        durationSeconds,
        aspectRatio: settings.aspect_ratio ?? null,
        generationCount,
        gifFrameRate: settings.gif_frame_rate ?? defaultSettings.gifFrameRate ?? null,
        gifMaxWidth: settings.gif_max_width ?? defaultSettings.gifMaxWidth ?? null,
        screenshotTimestamp:
            settings.screenshot_timestamp ?? defaultSettings.screenshotTimestamp ?? null,
        extendActionTag: settings.extend_ai_model_id ? null : settings.extend_action_tag ?? null,
        extendModelDisplayName:
            tag === 'extend' && pricedModel
                ? aiActionModelDisplayName(pricedModel, settings.extend_action_tag)
                : null,
        extendTimestamp: settings.extend_timestamp ?? null,
        smartStitch: settings.smart_stitch === true,
        hasTextInput: supportsTextInput && !!textValue,
        textInputValue: supportsTextInput ? textValue : null,
        textInputIsDefault: sourceAction?.prompt_is_default === true,
        hasImageInput: supportsImageInput && !!displayImageInput?.url,
        imageInputUrl: supportsImageInput ? displayImageInput?.url || null : null,
        referenceImageInputs: supportsReferenceInputs
            ? displayReferenceImages
                  .map((image, index) => ({
                      id: image?.id || `reference-image-${index}-${image?.url || ''}`,
                      url: image?.url,
                      label:
                          image?.label || (keyframes.frameMode ? 'End frame' : 'Reference image'),
                  }))
                  .filter(image => !!image.url)
            : [],
        videoKeyframeMode: keyframes.frameMode,
        hasVideoInput: supportsVideoInput && !!inputs?.video?.url,
        videoInputUrl: supportsVideoInput ? inputs?.video?.url || null : null,
        videoPreviewImageUrl: supportsVideoInput ? inputs?.video?.preview_image_url || null : null,
        videoInputs: supportsVideoListInput
            ? (inputs?.videos || (tag === 'stitch' ? references?.videos || [] : []))
                  .map((video, index) => ({
                      id: video?.id || `video-input-${index}-${video?.url || ''}`,
                      url: video?.url,
                      label: video?.label || `Video ${index + 1}`,
                      previewImageUrl: video?.preview_image_url || null,
                      actionTag: video?.action_tag || null,
                      durationSeconds:
                          video?.duration_seconds ??
                          video?.durationSeconds ??
                          video?.duration ??
                          null,
                  }))
                  .filter(video => !!video.url)
            : [],
        referenceVideoInputs:
            supportsReferenceInputs && !keyframes.frameMode
                ? (references?.videos || [])
                      .map((video, index) => ({
                          id: video?.id || `reference-video-${index}-${video?.url || ''}`,
                          url: video?.url,
                          label: video?.label || 'Reference video',
                          previewImageUrl: video?.preview_image_url || null,
                          actionTag: video?.action_tag || null,
                          durationSeconds:
                              video?.duration_seconds ??
                              video?.durationSeconds ??
                              video?.duration ??
                              null,
                      }))
                      .filter(video => !!video.url)
                : [],
        hasAudioInput: supportsAudioInput && !!inputs?.audio,
        audioInputLabel: supportsAudioInput ? inputs?.audio?.label || null : null,
        referenceAudioInputs:
            supportsReferenceInputs && !keyframes.frameMode
                ? (references?.audios || [])
                      .map((audio, index) => ({
                          id: audio?.id || `reference-audio-${index}-${audio?.url || ''}`,
                          url: audio?.url,
                          label: audio?.label || 'Reference audio',
                      }))
                      .filter(audio => !!audio.url)
                : [],
        hasLyrics: !!settings.lyrics,
        lyrics: settings.lyrics || null,
        inputOrder: Array.isArray(sourceAction?.input_order) ? sourceAction.input_order : null,
        supportsTextInput: supportsTextInput && !!textValue,
        supportsImageInput: supportsImageInput && !!displayImageInput?.url,
        supportsVideoInput: supportsVideoInput && !!inputs?.video?.url,
        supportsAudioInput: supportsAudioInput && !!inputs?.audio,
        supportsReferenceInputs:
            supportsReferenceInputs &&
            !!(
                references?.images?.length ||
                references?.videos?.length ||
                references?.audios?.length ||
                keyframes.endImage
            ),
        syncWithDraft: false,
        fullWidth: true,
    }
}

export const getAnswerActionTokenPreview = ({
    answer,
    aiModels,
    quest,
    tag,
    actionId = null,
    actionIndex = null,
}) => {
    const normalizedTag = normalizeActionTag(tag)
    if (!normalizedTag)
        return { run: null, action: null, actionRequestIndex: -1, previewProps: null }

    const actions = Array.isArray(answer?.action_requests) ? answer.action_requests : []
    const runs = Array.isArray(answer?.ai_runs) ? answer.ai_runs : []
    const numberedActionIndex = Number.isInteger(actionIndex) ? actionIndex : null
    const tagMatchedActions = actions.filter(
        candidate => normalizeActionTag(candidate?.tag) === normalizedTag,
    )
    const structuredAction =
        (actionId &&
            actions.find(
                candidate =>
                    candidate?.id === actionId ||
                    candidate?.client_id === actionId ||
                    candidate?.ai_action_id === actionId,
            )) ||
        (numberedActionIndex != null &&
        normalizeActionTag(actions[numberedActionIndex]?.tag) === normalizedTag
            ? actions[numberedActionIndex]
            : null) ||
        (tagMatchedActions.length === 1 ? tagMatchedActions[0] : null) ||
        null
    const action =
        structuredAction ||
        // An arbitrary "!word" the user typed must not adopt the answer body
        // as its prompt — that duplicates the visible text inside the token.
        (isKnownActionTag(normalizedTag, aiModels)
            ? legacyActionFromAnswerContent(answer, normalizedTag)
            : null)
    const run =
        (action?.ai_run_id && runs.find(candidate => candidate?.id === action.ai_run_id)) ||
        (actionId &&
            runs.find(
                candidate =>
                    candidate?.action_payload?.id === actionId ||
                    candidate?.action_payload?.client_id === actionId,
            )) ||
        (numberedActionIndex != null
            ? runs.find(
                  candidate =>
                      candidate?.action_index === numberedActionIndex &&
                      normalizeActionTag(candidate?.action_tag) === normalizedTag,
              )
            : null) ||
        null

    return {
        run,
        // The structured action_requests entry backing this token (null for legacy
        // content-derived actions); carries the metadata_public sharing flag and its
        // index for share-toggle writes.
        action: structuredAction,
        actionRequestIndex: structuredAction ? actions.indexOf(structuredAction) : -1,
        previewProps: aiActionToPreviewProps({ action, run, aiModels, quest }),
    }
}
